import React, { useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { Check, MessageSquare } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { UserContext } from '../UserContext';
import posthog from '../posthogClient';

const PAGE_NAMES = [
  [/^\/workspace\/[^/]+$/, 'Workspace'],
  [/^\/basket\/[^/]+$/, 'Basket'],
  [/^\/topic-detail\/[^/]+$/, 'Topic'],
  [/^\/workspace$/, 'Home'],
];

const getPageName = (pathname, view) => {
  const match = PAGE_NAMES.find(([pattern]) => pattern.test(pathname));
  const base = match ? match[1] : pathname;
  if (base === 'Workspace' && view) return `Workspace · ${view}`;
  return base;
};

const FeedbackField = ({ view = '' }) => {
  const { user } = useContext(UserContext);
  const location = useLocation();
  const inputId = useId();
  const triggerRef = useRef(null);
  const dialogRef = useRef(null);
  const textareaRef = useRef(null);
  const thanksRef = useRef(null);
  const closeTimerRef = useRef(null);
  const sentTimerRef = useRef(null);
  const isMountedRef = useRef(true);

  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);
  const [showThanks, setShowThanks] = useState(false);
  const [showSentTrigger, setShowSentTrigger] = useState(false);

  const pageName = getPageName(location.pathname, view);
  const route = `${location.pathname}${location.search}`;
  const trimmedMessage = message.trim();
  const sendDisabled = !trimmedMessage || isSending;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearTimeout(closeTimerRef.current);
      clearTimeout(sentTimerRef.current);
    };
  }, []);

  const focusTrigger = () => {
    window.requestAnimationFrame(() => {
      triggerRef.current?.focus();
    });
  };

  const handleShowSentTrigger = useCallback(() => {
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
    setIsOpen(false);
    setShowThanks(false);
    setShowSentTrigger(true);
    focusTrigger();
    clearTimeout(sentTimerRef.current);
    sentTimerRef.current = setTimeout(() => {
      setShowSentTrigger(false);
    }, 1500);
  }, []);

  const handleClose = useCallback(() => {
    if (showThanks) {
      handleShowSentTrigger();
      return;
    }
    setIsOpen(false);
    setSendFailed(false);
    focusTrigger();
  }, [handleShowSentTrigger, showThanks]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (!isOpen || showThanks) return undefined;
    textareaRef.current?.focus();
    return undefined;
  }, [isOpen, showThanks]);

  useEffect(() => {
    if (!isOpen || !showThanks) return undefined;
    thanksRef.current?.focus();
    return undefined;
  }, [isOpen, showThanks]);

  const handleTriggerClick = () => {
    clearTimeout(sentTimerRef.current);
    setShowSentTrigger(false);
    setIsOpen(true);
  };

  const handleMessageChange = (event) => {
    setMessage(event.target.value);
    if (sendFailed) setSendFailed(false);
  };

  const handleSubmit = async (event) => {
    event?.preventDefault();
    if (!trimmedMessage || isSending || showThanks || !user?.id) return;

    setIsSending(true);
    setSendFailed(false);

    const { error } = await supabase.from('feedback').insert({
      user_id: user.id,
      page: pageName,
      route,
      message: trimmedMessage,
    });

    if (!isMountedRef.current) return;

    if (error) {
      console.error('Error sending feedback:', error);
      setIsSending(false);
      setSendFailed(true);
      return;
    }

    if (process.env.REACT_APP_POSTHOG_KEY) {
      try {
        posthog.capture('feedback_sent', {
          page: pageName,
          route,
          length: trimmedMessage.length,
        });
      } catch (captureError) {
        console.warn('PostHog capture failed:', captureError);
      }
    }

    setMessage('');
    setIsSending(false);
    setShowThanks(true);
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      handleShowSentTrigger();
    }, 1100);
  };

  const handleTextareaKeyDown = (event) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      handleSubmit();
    }
  };

  const handleDialogKeyDown = (event) => {
    if (event.key !== 'Tab' || !dialogRef.current) return;
    const focusable = dialogRef.current.querySelectorAll(
      'button:not([disabled]), textarea, a[href], input, select'
    );
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!user) return null;

  const dialog = isOpen
    ? createPortal(
        <div className="fixed inset-0 z-[70] flex items-start justify-center pt-[12vh] md:items-center md:pt-0">
          <button
            type="button"
            tabIndex={-1}
            className="absolute inset-0 bg-neutral-900/35"
            aria-label="Close"
            onClick={handleClose}
          />
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-label="Feedback"
            onKeyDown={handleDialogKeyDown}
            className="relative w-[min(560px,calc(100%-32px))] rounded-2xl border-2 border-black bg-white p-4 shadow-[6px_6px_0_#171717]"
          >
            {showThanks ? (
              <div
                ref={thanksRef}
                tabIndex={-1}
                className="flex flex-col items-center py-8 text-center outline-none"
              >
                <div className="flex h-16 w-16 items-center justify-center rounded-full border-[3px] border-[#C9982E] bg-[#9f1239]">
                  <Check size={28} className="text-white" strokeWidth={2.5} aria-hidden="true" />
                </div>
                <p className="mt-4 text-lg font-bold text-neutral-900">Sent. Thank you.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <label htmlFor={inputId} className="sr-only">
                  What&apos;s clunky, or what would make it better?
                </label>
                <textarea
                  ref={textareaRef}
                  id={inputId}
                  rows={5}
                  maxLength={1000}
                  value={message}
                  onChange={handleMessageChange}
                  onKeyDown={handleTextareaKeyDown}
                  placeholder="What's clunky, or what would make it better?"
                  className="w-full resize-none border-0 bg-transparent text-[17px] leading-relaxed text-neutral-900 outline-none focus:ring-0"
                />
                {sendFailed && (
                  <p role="alert" className="mt-2 text-sm text-[#9f1239]">
                    That didn&apos;t send. Try again?
                  </p>
                )}
                <div className="mt-3 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="min-h-11 rounded-lg px-3 text-sm font-medium text-neutral-700 hover:text-neutral-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9f1239]"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={sendDisabled}
                    className={`
                      min-h-11 rounded-lg px-5 font-bold
                      focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9f1239] focus-visible:ring-offset-2
                      ${
                        sendDisabled
                          ? 'cursor-not-allowed border-2 border-neutral-300 bg-neutral-100 text-neutral-500'
                          : 'border-2 border-black bg-[#9f1239] text-white'
                      }
                    `}
                  >
                    Send
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleTriggerClick}
        aria-label={showSentTrigger ? 'Sent' : 'Clunky? Got an idea?'}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        className="inline-flex h-11 w-auto shrink-0 cursor-text items-center gap-2 rounded-full border-2 border-neutral-300 bg-neutral-100 px-3 text-sm text-neutral-600 sm:w-[230px]"
      >
        {showSentTrigger ? (
          <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[#C9982E] bg-[#fdf6e3]">
            <Check size={12} className="text-[#9f1239]" strokeWidth={3} aria-hidden="true" />
          </span>
        ) : (
          <MessageSquare size={16} className="shrink-0" aria-hidden="true" />
        )}
        <span className="sm:hidden">{showSentTrigger ? 'Sent' : 'Clunky?'}</span>
        <span className="hidden truncate sm:inline">
          {showSentTrigger ? 'Sent' : 'Clunky? Got an idea?'}
        </span>
      </button>
      {dialog}
    </>
  );
};

export default FeedbackField;
